import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import {
  type ArmMegaSecurity,
  type ValidateArmPoolRequest,
  ValidateArmPoolService,
} from './validate-arm-pool.service';

class PoolFileValidationError extends Error {}

@Component({
  standalone: false,
  selector: 'invd-validate-arm-pool',
  templateUrl: './validate-arm-pool.component.html',
  styleUrls: ['./validate-arm-pool.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidateArmPoolComponent {
  protected isModalOpen = true;
  protected showValidationAlert = false;
  protected isSuccessAlert = false;
  protected validationAlertMessage = 'Please input the required information.';
  protected hasFileTypeValidationError = false;
  protected hasPoolFileValidationError = false;
  protected hasPeriodValidationError = false;
  protected hasCollateralGroupValidationError = false;
  protected selectedPoolFileName = 'No file chosen';
  protected collateralGroupNumberValue = '0';
  protected detailed = true;
  protected isSubmitting = false;
  protected periodMonthPickerOpen = false;
  protected periodPickerYear = new Date().getFullYear();
  protected selectedPeriodDate: Date | null = null;

  protected monthNames = [
    { index: 0, short: 'Jan' },
    { index: 1, short: 'Feb' },
    { index: 2, short: 'Mar' },
    { index: 3, short: 'Apr' },
    { index: 4, short: 'May' },
    { index: 5, short: 'Jun' },
    { index: 6, short: 'Jul' },
    { index: 7, short: 'Aug' },
    { index: 8, short: 'Sep' },
    { index: 9, short: 'Oct' },
    { index: 10, short: 'Nov' },
    { index: 11, short: 'Dec' },
  ];

  private readonly elementRef = inject(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly validateArmPoolService = inject(ValidateArmPoolService);
  private readonly acceptedFileExtensions = new Set(['txt', 'csv', 'xls', 'xlsx']);
  private readonly periodFormatRegex = /^(0[1-9]|1[0-2])\/\d{4}$/;
  private selectedPoolFile: File | null = null;
  private hasSubmitted = false;

  protected onPoolFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.selectedPoolFile = file || null;
    this.selectedPoolFileName = file?.name || 'No file chosen';
    this.updateValidationFlags();
    this.showValidationAlert = false;
    this.isSuccessAlert = false;
    this.cdr.markForCheck();
  }

  protected onCollateralGroupNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.collateralGroupNumberValue = input.value;
    this.updateValidationFlags();
    this.showValidationAlert = false;
    this.isSuccessAlert = false;
    this.cdr.markForCheck();
  }

  protected onCollateralGroupNumberClick(event: MouseEvent): void {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  protected onDetailedChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.detailed = input.checked;
  }

  protected onReset(fileInput: HTMLInputElement): void {
    this.selectedPoolFile = null;
    this.selectedPoolFileName = 'No file chosen';
    this.collateralGroupNumberValue = '0';
    this.detailed = true;
    this.selectedPeriodDate = null;
    this.periodPickerYear = new Date().getFullYear();
    this.periodMonthPickerOpen = false;
    this.showValidationAlert = false;
    this.isSuccessAlert = false;
    this.hasFileTypeValidationError = false;
    this.hasPoolFileValidationError = false;
    this.hasPeriodValidationError = false;
    this.hasCollateralGroupValidationError = false;
    this.hasSubmitted = false;
    fileInput.value = '';
    this.cdr.markForCheck();
  }

  protected async onEmailReport(): Promise<void> {
    if (!this.validateForm()) {
      this.isSuccessAlert = false;
      this.validationAlertMessage = 'Please input the required information.';
      this.showValidationAlert = true;
      this.cdr.markForCheck();
      return;
    }

    this.showValidationAlert = false;
    this.isSuccessAlert = false;

    this.isSubmitting = true;
    this.cdr.markForCheck();

    try {
      const securites = await this.parseSecurities(this.selectedPoolFile as File);
      if (!securites.length) {
        this.isSuccessAlert = false;
        this.validationAlertMessage =
          'The uploaded file does not contain valid CUSIP and Original Face Amount rows.';
        this.showValidationAlert = true;
        this.cdr.markForCheck();
        return;
      }

      const payload: ValidateArmPoolRequest = {
        reportingPeriod: this.periodDisplayValue,
        collateralGroupNumber: Number(this.collateralGroupNumberValue),
        detailed: this.detailed,
        securites,
      };

      await firstValueFrom(this.validateArmPoolService.generateReport(payload));
      this.isSuccessAlert = true;
      this.validationAlertMessage =
        'Successfully submitted. You will be emailed the report once it is ready.';
      this.showValidationAlert = true;
      this.cdr.markForCheck();
    } catch (error) {
      if (error instanceof PoolFileValidationError) {
        this.isSuccessAlert = false;
        this.validationAlertMessage = error.message;
        this.showValidationAlert = true;
        this.cdr.markForCheck();
        return;
      }

      console.error('Failed to generate ARM pool report', error);
      this.isSuccessAlert = false;
      this.validationAlertMessage =
        'There was an issue processing your request. Please try again later.';
      this.showValidationAlert = true;
      this.cdr.markForCheck();
    } finally {
      this.isSubmitting = false;
      this.cdr.markForCheck();
    }
  }

  protected togglePeriodMonthPicker(event: MouseEvent): void {
    event.stopPropagation();
    this.periodMonthPickerOpen = !this.periodMonthPickerOpen;
  }

  protected changePeriodYear(delta: number, event: MouseEvent): void {
    event.stopPropagation();
    this.periodPickerYear += delta;
  }

  protected isSelectedPeriodMonth(monthIndex: number): boolean {
    return (
      !!this.selectedPeriodDate &&
      this.selectedPeriodDate.getFullYear() === this.periodPickerYear &&
      this.selectedPeriodDate.getMonth() === monthIndex
    );
  }

  protected selectPeriodMonth(monthIndex: number, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedPeriodDate = new Date(this.periodPickerYear, monthIndex, 1);
    this.periodMonthPickerOpen = false;
    this.updateValidationFlags();
    this.showValidationAlert = false;
    this.isSuccessAlert = false;
    this.cdr.markForCheck();
  }

  protected get periodDisplayValue(): string {
    if (!this.selectedPeriodDate) {
      return '';
    }

    const month = String(this.selectedPeriodDate.getMonth() + 1).padStart(2, '0');
    return `${month}/${this.selectedPeriodDate.getFullYear()}`;
  }

  private validateForm(): boolean {
    this.hasSubmitted = true;
    const { isFileSelected, isFileTypeValid, isPeriodValid, isCollateralGroupNumberValid } =
      this.updateValidationFlags();

    return isFileSelected && isFileTypeValid && isPeriodValid && isCollateralGroupNumberValid;
  }

  private updateValidationFlags(): {
    isFileSelected: boolean;
    isFileTypeValid: boolean;
    isPeriodValid: boolean;
    isCollateralGroupNumberValid: boolean;
  } {
    const isFileSelected = !!this.selectedPoolFile;
    const isFileTypeValid = isFileSelected
      ? this.isAcceptedFileType(this.selectedPoolFile as File)
      : false;
    const isPeriodValid = this.periodFormatRegex.test(this.periodDisplayValue);
    const isCollateralGroupNumberValid =
      this.collateralGroupNumberValue.trim() === '' ||
      !Number.isInteger(Number(this.collateralGroupNumberValue)) ||
      Number(this.collateralGroupNumberValue) < 0
        ? false
        : true;

    this.hasFileTypeValidationError = isFileSelected && !isFileTypeValid;
    this.hasPoolFileValidationError = this.hasSubmitted && (!isFileSelected || !isFileTypeValid);
    this.hasPeriodValidationError = this.hasSubmitted && !isPeriodValid;
    this.hasCollateralGroupValidationError = this.hasSubmitted && !isCollateralGroupNumberValid;

    return { isFileSelected, isFileTypeValid, isPeriodValid, isCollateralGroupNumberValid };
  }

  private isAcceptedFileType(file: File): boolean {
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
    return this.acceptedFileExtensions.has(fileExtension);
  }

  private async parseSecurities(file: File): Promise<ArmMegaSecurity[]> {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';

    if (extension === 'txt' || extension === 'csv') {
      const text = await file.text();
      return this.parseDelimitedRows(text);
    }

    if (extension === 'xls' || extension === 'xlsx') {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        return [];
      }

      const sheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, {
        header: 1,
        blankrows: false,
      });

      return this.parseTabularRows(rows);
    }

    throw new Error('Unsupported file type. Accepted formats: txt, csv, xls, xlsx.');
  }

  private parseDelimitedRows(content: string): ArmMegaSecurity[] {
    const lines = content
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    const rows = lines.map((line) => {
      if (line.includes(',')) {
        return line.split(',').map((cell) => cell.trim());
      }
      if (line.includes('\t')) {
        return line.split('\t').map((cell) => cell.trim());
      }
      if (line.includes('|')) {
        return line.split('|').map((cell) => cell.trim());
      }
      return line.split(/\s+/).map((cell) => cell.trim());
    });

    return this.parseTabularRows(rows);
  }

  private parseTabularRows(rows: Array<Array<string | number | null>>): ArmMegaSecurity[] {
    if (!rows.length) {
      return [];
    }

    const header = rows[0].map((cell) => this.normalizeHeader(cell));
    const hasHeader = header.some((cell) => this.isCusipHeader(cell) || cell.includes('face'));

    const cusipIndex = hasHeader ? header.findIndex((cell) => this.isCusipHeader(cell)) : 0;
    const amountIndex = hasHeader ? this.findAmountColumnIndex(header) : 1;

    const startIndex = hasHeader ? 1 : 0;
    const securites: ArmMegaSecurity[] = [];

    for (let i = startIndex; i < rows.length; i++) {
      const row = rows[i];
      const cusip = String(row[cusipIndex] ?? '').trim();

      if (!cusip) {
        continue;
      }

      if (!this.isValidCusip(cusip)) {
        throw new PoolFileValidationError(
          `Row ${i + 1} contains an invalid CUSIP. CUSIPs must be nine uppercase letters or digits with a valid check digit.`,
        );
      }

      const amount = this.parseAmount(row[amountIndex]);
      if (!this.isValidOriginalFaceAmount(row[amountIndex], amount)) {
        throw new PoolFileValidationError(
          `Row ${i + 1} has an invalid Original Face Amount. It must be an integer greater than 0.`,
        );
      }

      securites.push({
        cusip,
        originalFaceAmount: amount,
      });
    }

    return securites.slice(0, 1000);
  }

  private isValidCusip(cusip: string): boolean {
    if (!/^[A-Z0-9]{9}$/.test(cusip)) {
      return false;
    }

    const checksum = [...cusip.slice(0, 8)].reduce((sum, character, index) => {
      const value = Number.isNaN(Number(character))
        ? character.charCodeAt(0) - 'A'.charCodeAt(0) + 10
        : Number(character);
      const product = value * (index % 2 === 0 ? 1 : 2);
      return sum + Math.floor(product / 10) + (product % 10);
    }, 0);

    return (10 - (checksum % 10)) % 10 === Number(cusip[8]);
  }

  private isCusipHeader(value: string): boolean {
    return (
      value === 'cusip' || value === 'cusp' || value.startsWith('cusip') || value.startsWith('cusp')
    );
  }

  private findAmountColumnIndex(header: string[]): number {
    if (
      header.length === 1 &&
      this.isCusipHeader(header[0]) &&
      header[0].includes('originalface')
    ) {
      return 1;
    }

    const originalIndex = header.findIndex(
      (cell, index) => cell === 'original' && header[index + 1] === 'face',
    );
    if (originalIndex >= 0) {
      return originalIndex;
    }

    const amountIndex = header.findIndex(
      (cell) =>
        cell.includes('originalfaceamount') ||
        cell.includes('faceamount') ||
        cell.includes('originalface') ||
        cell.includes('contributionamount') ||
        cell.includes('amount'),
    );

    if (amountIndex >= 0) {
      return amountIndex;
    }

    return 1;
  }

  private isValidOriginalFaceAmount(
    value: string | number | null,
    amount: number | null,
  ): amount is number {
    if (amount === null || !Number.isInteger(amount) || amount <= 0) {
      return false;
    }

    if (typeof value === 'string') {
      return /^\d+$/.test(value.replace(/[$,\s]/g, ''));
    }

    return true;
  }

  private normalizeHeader(value: string | number | null): string {
    return String(value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  private parseAmount(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const normalized = String(value).replace(/[$,\s]/g, '');
    if (!normalized) {
      return null;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: Event): void {
    const periodContainer = this.elementRef.nativeElement.querySelector(
      '.validate-arm-pool-period-field',
    ) as HTMLElement | null;

    if (
      this.periodMonthPickerOpen &&
      periodContainer &&
      !periodContainer.contains(event.target as Node)
    ) {
      this.periodMonthPickerOpen = false;
    }
  }
}
